import React, { useState, useEffect, useCallback } from 'react';
import { 
	Box, 
	Paper, 
	Typography, 
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Chip,
	IconButton,
	Tooltip,
	Grid
} from '@mui/material';
import { 
	Visibility as VisibilityIcon,
	Delete as DeleteIcon
} from '@mui/icons-material';
import { IManagerInitiationData } from '../interfaces/IOnboardingFlow';
import {
	ManagerInitiationForm,
} from '../components';
import style from './OnboardingDashboard.module.scss';
import { createOnboarding, getOnboardings, deleteOnboarding, OnboardingRecord } from 'core/API/onboarding';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';
import { Button } from '../../../components/ui/button';
import EmployeeOnboardingReview from './EmployeeOnboardingReview';

interface OnboardingDashboardProps {
	workplaceId: string;
	organizationId: string;
}

// Helper functions to format display values
const formatPayType = (value: string): string => {
	if (!value) return value;
	const lowerValue = value.toLowerCase();
	if (lowerValue === 'full_time' || lowerValue === 'ft') return 'Full Time';
	if (lowerValue === 'part_time' || lowerValue === 'pt') return 'Part Time';
	return value; // Fallback to original value
};

const formatEmploymentType = (value: string): string => {
	if (!value) return value;
	const lowerValue = value.toLowerCase();
	if (lowerValue === 'hourly') return 'Hourly';
	if (lowerValue === 'salary') return 'Salary';
	return value; // Fallback to original value
};

export default function OnboardingDashboard({
	workplaceId,
	organizationId: _organizationId,
}: OnboardingDashboardProps): JSX.Element {
	const [onboardingRecords, setOnboardingRecords] = useState<OnboardingRecord[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [currentView, setCurrentView] = useState<
		'dashboard' | 'create' | 'view' | 'employee-entry' | 'manager-review' | 'review-progress'
	>('dashboard');
	const [selectedRecord, setSelectedRecord] = useState<OnboardingRecord | null>(null);
	const [_totalItems, setTotalItems] = useState(0);
	const [page, _setPage] = useState(1);
	const [itemsPerPage] = useState(30);
	const { showToast } = useToastContext();

	// Permission checks for Onboarding module
	const { hasModuleExecutePermission, hasModuleDeletePermission } = usePermissionCheck();
	const canExecuteOnboarding = hasModuleExecutePermission('Onboarding');
	const canDeleteOnboarding = hasModuleDeletePermission('Onboarding');

	// Fetch onboarding records
	const fetchOnboardingRecords = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await getOnboardings(Number(workplaceId), page, itemsPerPage);
			const records = response.member || response['hydra:member'] || [];
			const total = response.totalItems || response['hydra:totalItems'] || 0;
			
			setOnboardingRecords(records);
			setTotalItems(total);
		} catch (error) {
			console.error('Error fetching onboarding records:', error);
			showToast('error', 'Failed to fetch onboarding records');
		} finally {
			setIsLoading(false);
		}
	}, [workplaceId, page, itemsPerPage, showToast]);

	useEffect(() => {
		fetchOnboardingRecords();
	}, [fetchOnboardingRecords]);

	// Handler for creating new onboarding
	const handleCreateOnboarding = () => {
		setCurrentView('create');
		setSelectedRecord(null);
	};

	// Handler for submitting Phase 1 (Manager Initiation)
	const handleSubmitManagerInitiation = async (data: IManagerInitiationData) => {
		try {
			// Map form data to API payload
			const payload = {
				user: data.existingEmployeeId || undefined, // Optional, for existing/rehire employees
				firstName: data.firstName,
				middleName: data.middleName || '',
				lastName: data.lastName,
				email: data.email,
				cellPhone: data.cellPhone,
				type: data.employeeType, // 'new', 'rehire', 'existing'
				workplace: `/api/workplaces/${workplaceId}`,
				employmentStartDate: new Date(data.employmentStartDate).toISOString(),
				payType: data.roleType, // 'part_time' or 'full_time'
				employmentType: data.employmentType, // 'hourly' or 'salary'
				securityRole: data.securityRole, // Already an IRI
				hierarchyAssignment: data.hierarchyAssignment, // Hierarchy IRI (required)
			};

			// Call API to create onboarding
			const response = await createOnboarding(payload);
			console.log('Onboarding created successfully:', response);
			
			showToast('success', 'Onboarding created successfully!');
			
			// Refresh the list
			await fetchOnboardingRecords();
			
			// Return to dashboard
			setCurrentView('dashboard');
		} catch (error: any) {
			console.error('Error creating onboarding record:', error);
			showToast(
				'error',
				error?.response?.data?.message || 'Failed to create onboarding record. Please try again.'
			);
		}
	};

	// Handler for opening a record for review/edit
	const handleOpenRecord = (record: OnboardingRecord) => {
		setSelectedRecord(record);
		setCurrentView('review-progress');
	};

	// Handler for deleting a record
	const handleDeleteRecord = async (id: number) => {
		if (!window.confirm('Are you sure you want to delete this onboarding record?')) {
			return;
		}
		
		try {
			await deleteOnboarding(id);
			showToast('success', 'Onboarding record deleted successfully');
			await fetchOnboardingRecords();
		} catch (error: any) {
			console.error('Error deleting onboarding record:', error);
			showToast('error', 'Failed to delete onboarding record');
		}
	};

	// Handler for canceling form
	const handleCancelForm = () => {
		setCurrentView('dashboard');
		setSelectedRecord(null);
	};

	// Get status chip color
	const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' => {
		switch (status?.toLowerCase()) {
			case 'completed':
				return 'success';
			case 'in_progress':
				return 'primary';
			case 'pending':
				return 'warning';
			case 'draft':
				return 'default';
			default:
				return 'default';
		}
	};

	// Format date for display
	const formatDate = (dateString: string | undefined) => {
		if (!dateString) return 'N/A';
		try {
			return new Date(dateString).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric'
			});
		} catch {
			return 'Invalid Date';
		}
	};

	return (
		<div className={style.DashboardContainer}>
			{/* Header - only show on dashboard view */}
			{currentView === 'dashboard' && canExecuteOnboarding && (
				<div className={style.Header}>
					<Button onClick={handleCreateOnboarding} className={style.CreateButton}>
						Add
					</Button>
				</div>
			)}

			{/* Dashboard View */}
			{currentView === 'dashboard' && (
				<>
					{isLoading ? (
						<Box sx={{ textAlign: 'center', padding: '3rem' }}>
							<CircularProgress />
						</Box>
					) : (
						<>
							{/* Phase 1 Complete: Manager Initiated */}
							<Paper sx={{ p: 3, mb: 3 }}>
								<Box sx={{ mb: 2 }}>
								<Typography variant="h6" sx={{ color: 'rgb(68, 99, 136)' }}>
										Phase 1: Manager Initiated - Waiting for Employee (
								{onboardingRecords.filter(r => r.phase1CompletedAt && !r.phase2CompletedAt).length})
							</Typography>
						</Box>
						{onboardingRecords.filter(r => r.phase1CompletedAt && !r.phase2CompletedAt).length === 0 ? (
									<Box sx={{ textAlign: 'center', padding: '2rem' }}>
										<Typography sx={{ color: '#999' }}>No records in Phase 1</Typography>
									</Box>
								) : (
						<TableContainer sx={{ 
							maxHeight: 'calc(100vh - 300px)',
							'&::-webkit-scrollbar': {
								width: '4px',
								height: '4px',
							},
							'&::-webkit-scrollbar-track': {
								background: '#f1f1f1',
								borderRadius: '2px',
							},
							'&::-webkit-scrollbar-thumb': {
								background: '#888',
								borderRadius: '2px',
								'&:hover': {
									background: '#555',
								},
							},
						}}>
							<Table stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Email</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Phone</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Type</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Status</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Employment Start</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Pay Type</TableCell>
									<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Employment Type</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'center', width: '120px', minWidth: '120px' }}>Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
							{onboardingRecords.filter(r => r.phase1CompletedAt && !r.phase2CompletedAt).map((record) => (
										<TableRow
											key={record.id}
											sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
										>
											<TableCell>
												{`${record.firstName} ${record.middleName ? record.middleName + ' ' : ''}${record.lastName}`}
											</TableCell>
											<TableCell>{record.email}</TableCell>
											<TableCell>{record.cellPhone}</TableCell>
											<TableCell>
											<Chip label={record.type} size="small" />
											</TableCell>
											<TableCell>
												<Chip 
													label={record.status.replace('_', ' ').toUpperCase()} 
													size="small" 
													color={getStatusColor(record.status)}
												/>
											</TableCell>
											<TableCell>{formatDate(record.employmentStartDate)}</TableCell>
											<TableCell>
										<Chip label={formatPayType(record.payType)} size="small" />
									</TableCell>
									<TableCell>
										<Chip label={formatEmploymentType(record.employmentType)} size="small" />
										</TableCell>
									<TableCell sx={{ textAlign: 'center', width: '120px', minWidth: '120px' }}>
												<Tooltip title="View Details">
													<IconButton
														size="small"
														onClick={() => handleOpenRecord(record)}
														sx={{ color: 'rgb(68, 99, 136)' }}
													>
														<VisibilityIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												{canDeleteOnboarding && (
													<Tooltip title="Delete">
														<IconButton
															size="small"
															onClick={() => handleDeleteRecord(record.id)}
															sx={{ color: '#d32f2f' }}
														>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Paper>

				{/* Phase 2 Complete: Ready for Manager Review */}
				<Paper sx={{ p: 3, mb: 3 }}>
					<Box sx={{ mb: 2 }}>
						<Typography variant="h6" sx={{ color: 'rgb(68, 99, 136)' }}>
							Phase 2: Ready for Manager Review (
					{onboardingRecords.filter(r => r.phase2CompletedAt && !r.phase2ReviewedAt).length})
						</Typography>
					</Box>
			{onboardingRecords.filter(r => r.phase2CompletedAt && !r.phase2ReviewedAt).length === 0 ? (
						<Box sx={{ textAlign: 'center', padding: '2rem' }}>
							<Typography sx={{ color: '#999' }}>No records awaiting manager review</Typography>
						</Box>
					) : (
					<TableContainer sx={{ 
						maxHeight: 'calc(100vh - 300px)',
						'&::-webkit-scrollbar': {
							width: '4px',
							height: '4px',
						},
						'&::-webkit-scrollbar-track': {
							background: '#f1f1f1',
							borderRadius: '2px',
						},
						'&::-webkit-scrollbar-thumb': {
							background: '#888',
							borderRadius: '2px',
							'&:hover': {
								background: '#555',
							},
						},
					}}>
							<Table stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Email</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Phone</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Type</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Status</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Employment Start</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Pay Type</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Employment Type</TableCell>
								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'center', width: '120px', minWidth: '120px' }}>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
						{onboardingRecords.filter(r => r.phase2CompletedAt && !r.phase2ReviewedAt).map((record) => (
										<TableRow
											key={record.id}
											sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
										>
											<TableCell>
												{`${record.firstName} ${record.middleName ? record.middleName + ' ' : ''}${record.lastName}`}
											</TableCell>
											<TableCell>{record.email}</TableCell>
											<TableCell>{record.cellPhone}</TableCell>
											<TableCell>
											<Chip label={record.type} size="small" />
											</TableCell>
											<TableCell>
												<Chip 
													label={record.status.replace('_', ' ').toUpperCase()} 
													size="small" 
													color={getStatusColor(record.status)}
												/>
											</TableCell>
											<TableCell>{formatDate(record.employmentStartDate)}</TableCell>
											<TableCell>
											<Chip label={formatPayType(record.payType)} size="small" />
										</TableCell>
										<TableCell>
											<Chip label={formatEmploymentType(record.employmentType)} size="small" />
											</TableCell>										<TableCell>
											{record.phase2ReviewStatus ? (
												<Chip 
													label={record.phase2ReviewStatus.toUpperCase()} 
													size="small" 
													color={record.phase2ReviewStatus === 'approved' ? 'success' : 'error'}
												/>
											) : (
												<Chip label="PENDING" size="small" color="warning" />
											)}
										</TableCell>										<TableCell sx={{ textAlign: 'center', width: '120px', minWidth: '120px' }}>
												<Tooltip title="View Details">
													<IconButton
														size="small"
														onClick={() => handleOpenRecord(record)}
														sx={{ color: 'rgb(68, 99, 136)' }}
													>
														<VisibilityIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												{canDeleteOnboarding && (
													<Tooltip title="Delete">
														<IconButton
															size="small"
															onClick={() => handleDeleteRecord(record.id)}
															sx={{ color: '#d32f2f' }}
														>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Paper>

				{/* Phase 3 Complete: Manager Reviewed - Archive */}
				<Paper sx={{ p: 3 }}>
					<Box sx={{ mb: 2 }}>
					<Typography variant="h6" sx={{ color: 'rgb(68, 99, 136)' }}>
							Phase 3: Manager Reviewed - Archive ({onboardingRecords.filter(r => r.phase2ReviewedAt).length})
						</Typography>
					</Box>
				{onboardingRecords.filter(r => r.phase2ReviewedAt).length === 0 ? (
						<Box sx={{ textAlign: 'center', padding: '2rem' }}>
							<Typography sx={{ color: '#999' }}>No completed onboarding records</Typography>
						</Box>
					) : (
					<TableContainer sx={{ 
						maxHeight: 'calc(100vh - 300px)',
						'&::-webkit-scrollbar': {
							width: '4px',
							height: '4px',
						},
						'&::-webkit-scrollbar-track': {
							background: '#f1f1f1',
							borderRadius: '2px',
						},
						'&::-webkit-scrollbar-thumb': {
							background: '#888',
							borderRadius: '2px',
							'&:hover': {
								background: '#555',
							},
						},
					}}>
							<Table stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Email</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Phone</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Type</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Status</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Employment Start</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Pay Type</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Employment Type</TableCell>								<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Review Status</TableCell>										<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'center', width: '120px', minWidth: '120px' }}>Actions</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
								{onboardingRecords.filter(r => r.phase2ReviewedAt).map((record) => (
										<TableRow
											key={record.id}
											sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
										>
											<TableCell>
												{`${record.firstName} ${record.middleName ? record.middleName + ' ' : ''}${record.lastName}`}
											</TableCell>
											<TableCell>{record.email}</TableCell>
											<TableCell>{record.cellPhone}</TableCell>
											<TableCell>
												<Chip label={record.type} size="small" />
											</TableCell>
											<TableCell>
												<Chip 
													label={record.status.replace('_', ' ').toUpperCase()} 
													size="small" 
													color={getStatusColor(record.status)}
												/>
											</TableCell>
											<TableCell>{formatDate(record.employmentStartDate)}</TableCell>
											<TableCell>
												<Chip label={formatPayType(record.payType)} size="small" />
											</TableCell>
											<TableCell>
												<Chip label={formatEmploymentType(record.employmentType)} size="small" />
											</TableCell>										<TableCell>
											{record.phase2ReviewStatus ? (
												<Chip 
													label={record.phase2ReviewStatus.toUpperCase()} 
													size="small" 
													color={record.phase2ReviewStatus === 'approved' ? 'success' : 'error'}
												/>
											) : (
												<Chip label="PENDING" size="small" color="warning" />
											)}
										</TableCell>											<TableCell sx={{ textAlign: 'center', width: '120px', minWidth: '120px' }}>
												<Tooltip title="View Details">
													<IconButton
														size="small"
														onClick={() => handleOpenRecord(record)}
														sx={{ color: 'rgb(68, 99, 136)' }}
													>
														<VisibilityIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												{canDeleteOnboarding && (
													<Tooltip title="Delete">
														<IconButton
															size="small"
															onClick={() => handleDeleteRecord(record.id)}
															sx={{ color: '#d32f2f' }}
														>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Paper>
			</>
			)}
		</>
		)}

		{/* Phase 1: Manager Initiation Form */}
			{currentView === 'create' && (
				<ManagerInitiationForm
					workplaceId={workplaceId}
					onSubmit={handleSubmitManagerInitiation}
					onCancel={handleCancelForm}
				/>
			)}

			{/* Employee Onboarding Progress Review */}
			{currentView === 'review-progress' && selectedRecord && (
				<EmployeeOnboardingReview
					record={selectedRecord}
					onBack={handleCancelForm}
					onRefresh={fetchOnboardingRecords}
				/>
			)}

			{/* View Onboarding Details (deprecated - use review-progress instead) */}
			{currentView === 'view' && selectedRecord && (
				<div className={style.DashboardContainer}>
					<Grid container direction="column" spacing={2} sx={{ pl: 2, pt: 2 }}>
						<Grid container direction="row" justifyContent="space-between">
							<Grid>
								<Button className={style.BackButton} onClick={handleCancelForm}>
									Back
								</Button>
							</Grid>
						</Grid>
						<Grid container direction="row" justifyContent="flex-start" alignItems="center">
							<h2 className={style.formTitle}>Onboarding Details</h2>
						</Grid>
					</Grid>

					<Paper sx={{ p: 3, m: 2, mt: 1 }}>
						<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
							<Box>
								<Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{`${selectedRecord.firstName} ${selectedRecord.middleName ? selectedRecord.middleName + ' ' : ''}${selectedRecord.lastName}`}
								</Typography>
							</Box>

							<Box>
								<Typography variant="subtitle2" color="text.secondary">Status</Typography>
								<Box sx={{ mb: 2 }}>
									<Chip 
										label={selectedRecord.status.replace('_', ' ').toUpperCase()} 
										color={getStatusColor(selectedRecord.status)}
									/>
								</Box>
							</Box>

							<Box>
								<Typography variant="subtitle2" color="text.secondary">Email</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>{selectedRecord.email}</Typography>
							</Box>

							<Box>
								<Typography variant="subtitle2" color="text.secondary">Cell Phone</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>{selectedRecord.cellPhone}</Typography>
							</Box>

							<Box>
								<Typography variant="subtitle2" color="text.secondary">Employee Type</Typography>
								<Box sx={{ mb: 2 }}>
									<Chip label={selectedRecord.type} size="small" />
								</Box>
							</Box>

							<Box>
								<Typography variant="subtitle2" color="text.secondary">Employment Start Date</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>{formatDate(selectedRecord.employmentStartDate)}</Typography>
							</Box>

							<Box>
								<Typography variant="subtitle2" color="text.secondary">Pay Type</Typography>
								<Box sx={{ mb: 2 }}>
								<Chip label={formatPayType(selectedRecord.payType)} size="small" />
							</Box>
						</Box>

						<Box>
							<Typography variant="subtitle2" color="text.secondary">Employment Type</Typography>
							<Box sx={{ mb: 2 }}>
								<Chip label={formatEmploymentType(selectedRecord.employmentType)} size="small" />
								</Box>
							</Box>
						</Box>
					</Paper>
				</div>
			)}

			{/* Phase 2 & 3: To be implemented with new API structure */}
			{/* {currentView === 'employee-entry' && selectedRecord && (
				<EmployeeDataEntryView
					record={selectedRecord}
					onClose={handleCancelForm}
				/>
			)} */}

			{/* {currentView === 'manager-review' && selectedRecord && (
				<ManagerReviewView
					record={selectedRecord}
					onComplete={async () => {
						await fetchOnboardingRecords();
						setCurrentView('dashboard');
						setSelectedRecord(null);
					}}
					onCancel={handleCancelForm}
				/>
			)} */}
		</div>
	);
}
